// =====================================================================
// Catalogue-driven procedure flag helpers — single source of truth.
// Replaces the WHOLE_TOOTH_CATEGORIES / WHOLE_TOOTH_NAME_RE constants
// that were duplicated in TreatmentTab.jsx + Doctor.jsx.
//
// The catalog now drives everything via flag columns (migration
// 20260606000001). Each helper prefers the explicit catalog flag and
// falls back to legacy heuristics so the frontend keeps working
// before the migration is applied.
// =====================================================================

// --- Whole-tooth detection ------------------------------------------
// Prefer procedures.surface_specific (false = whole-tooth).
// Fallback only fires when the column hasn't been backfilled yet.

const LEGACY_WHOLE_TOOTH_CATEGORIES = new Set([
  "Prosthodontic",
  "Endodontic",
  "Oral Surgery",
  "Implant",
]);
const LEGACY_WHOLE_TOOTH_NAME_RE =
  /\b(crown|veneer|bridge|extraction|root canal|pulpotomy|implant)\b/i;

export function isWholeToothProcedure(proc) {
  if (!proc) return false;
  if (typeof proc.surface_specific === "boolean") return !proc.surface_specific;
  // Legacy fallback (pre-20260606000001)
  if (LEGACY_WHOLE_TOOTH_CATEGORIES.has(proc.category)) return true;
  return LEGACY_WHOLE_TOOTH_NAME_RE.test(proc.name || "");
}

// --- Bare tooth-based front-desk booking -----------------------------
// A scheduled line the front desk booked as a plain reason for a
// tooth/surface procedure (composite, crown) with NO tooth recorded yet.
// Its flat catalog price is a one-surface guess, so marking it "Done" must
// not drop that charge — the real (surface-priced) work is recorded
// separately. Scoped to appointment_scheduled_item so it never touches plan
// work or a dentist-detailed future need (which already carries a tooth).
// Operates on a hydrated scheduled item (which carries tooth_specific /
// surface_specific from get_checkout_hydration).
export function isBareToothBooking(s) {
  return s?.source === "appointment_scheduled_item"
    && (s?.tooth_specific || s?.surface_specific)
    && !s?.tooth_number
    && !s?.treatment_plan_id
    && !s?.ortho_case_id;
}

// --- Multi-visit -----------------------------------------------------
// A procedure that normally spans more than one visit (RCT, crown,
// denture). The count is a SOFT estimate the dentist controls — this
// only answers "does this usually bring the patient back?", never
// "exactly how many visits." Mirrors the check in
// multiToothProcedureModel so both surfaces agree.

export function isMultiVisitProcedure(proc) {
  if (!proc) return false;
  return proc.multi_visit === true || (Number(proc.visit_count) || 1) > 1;
}

// A recalled Future Need is still a treatment_need when it returns on a booked
// appointment. Match it only when the doctor paints the same multi-visit
// catalog procedure on the same tooth/surface; ordinary Today paints must keep
// the guided three-choice prompt.
export function findScheduledMultiVisitNeedForPaint(
  scheduledItems,
  proc,
  toothNumber,
  surface,
) {
  if (!isMultiVisitProcedure(proc) || !Array.isArray(scheduledItems)) return null;

  const targetTooth = toothNumber == null ? "" : String(toothNumber);
  const targetSurface = surface == null ? "" : String(surface).toUpperCase();

  return scheduledItems.find((row) => {
    if (row?.source !== "treatment_need" || !row?.tn_id) return false;
    if (row?.status !== "Scheduled" || row?.procedure_id !== proc.id) return false;

    const rowTooth = row.tooth_number == null ? "" : String(row.tooth_number);
    const rowSurface = row.surface == null ? "" : String(row.surface).toUpperCase();
    if (rowTooth !== targetTooth) return false;
    if (rowSurface === targetSurface) return true;

    // A multi-surface need such as "MO" still matches the M or O surface the
    // doctor taps. Whole-tooth procedures naturally match on two empty values.
    return !!rowSurface && !!targetSurface
      && (rowSurface.includes(targetSurface) || targetSurface.includes(rowSurface));
  }) || null;
}

// --- Multi-tooth kind -----------------------------------------------
// Prefer procedures.multi_tooth_kind. Fallback to legacy regex only
// while clinics are migrating their catalog.

export function multiToothKindOf(proc) {
  if (!proc) return null;
  if (proc.multi_tooth_kind) return proc.multi_tooth_kind;
  // Legacy fallback patterns (mirror BuildMultiToothDialog's old
  // CATEGORY_DEFS so the dialog still buckets pre-migration rows).
  const name = proc.name || "";
  const stable = proc.stable_id || "";
  if (/\bbridge\b|\bsplint\b/i.test(name) || /^bridge|splint/i.test(stable)) return "bridge";
  if (/partial\s*denture/i.test(name) || /partial.*denture/i.test(stable)) return "partial_denture";
  if (/complete\s*denture|full\s*denture/i.test(name) || /complete.*denture|full.*denture/i.test(stable)) return "full_denture";
  if (
    /\b(crown|veneer)\b/i.test(name) &&
    !/recement|implant|stainless|ssc/i.test(name) &&
    proc.tooth_count === "single" &&
    (proc.requires_lab_work === true || proc.multi_visit === true)
  ) {
    return "crown_unit";
  }
  return null;
}

// --- Booking window (days) ------------------------------------------
// Prefer procedures.default_booking_window_days. Fallback to a per-
// category map mirroring the legacy CATEGORY_DEFAULT_DAYS used in
// BookFutureNeedDialog.

const LEGACY_CATEGORY_DEFAULT_DAYS = {
  Endodontic:    5,
  "Oral Surgery": 5,
  Restorative:   10,
  Pediatric:     10,
  Diagnostic:    10,
  Periodontal:   14,
  Prosthodontic: 18,
  Cosmetic:      18,
  Implant:       18,
  Orthodontic:   10,
  Preventive:    90,
  Emergency:     2,
};

export function defaultBookingWindowDays(proc) {
  if (!proc) return null;
  if (Number.isFinite(proc.default_booking_window_days)) {
    return proc.default_booking_window_days;
  }
  return LEGACY_CATEGORY_DEFAULT_DAYS[proc.category] ?? null;
}

// Render a human-readable hint from the picked days. Replaces the
// hardcoded category branches in BookFutureNeedDialog so the hint
// stays consistent with whatever value the catalogue serves.
export function bookingWindowHint(days) {
  if (!Number.isFinite(days) || days <= 0) return null;
  if (days <= 3)  return "Suggested: within a few days — urgent.";
  if (days <= 7)  return "Suggested: within 1 week.";
  if (days <= 14) return "Suggested: within 2 weeks.";
  if (days <= 21) return "Suggested: 2–3 weeks out.";
  if (days <= 60) return "Suggested: 3–8 weeks out.";
  return "Suggested: 3–6 months out.";
}
